import { gsap } from "gsap";

import { EventsManager, ReelsEvents } from "../../../abstracts/events/EventsManager";
import { AbstractGameMode } from "../../../abstracts/flow/AbstractGameMode";
import { LevelName, TimelineLabels, WinType } from "../../../abstracts/types/enums";
import { IWinLevel } from "../../../abstracts/types/interfaces";
import { DDD } from "../../DDD";
import { getWinLevel, winLevels } from "../../config/DDDConfig";
import { DDDReelsView } from "../../modules/Reels/DDDReelsView";
import { totalWinHandler } from "../decorators/DDDTotalWinHandler";
import { DDDNormalWinPresentation } from "../normal/DDDNormalWinPresentation";

export class DDDFreespinWinPresentation extends DDDNormalWinPresentation {
    protected reelsView: DDDReelsView;

    constructor(id: string = `DDDFreespinWinPresentation`) {
        super(id);
    }

    protected override finishPresentation() {
        super.finishPresentation();

        EventsManager.trigger(ReelsEvents.RESET_FAKE_SCATTERS); //might be moved from here
    }

    protected override handleWinnings() {
        const isAccumulatedWinBelowStake = DDD.model.isWinBelowStake(true);

        this.flowPresentationTimeline = gsap.timeline({
            onComplete: () => this.addSpecialAnimations(isAccumulatedWinBelowStake),
        });

        if (this.model.gameData.accumulatedRoundWin <= 0 && !DDD.model.xNudgeMap.includes(true)) {
            return;
        } else {
            let winValue = this.model.gameData.accumulatedRoundWin;
            if (this.model.gameData.brokeTheBank) {
                winValue =
                    this.model.gameData.accumulatedRoundWin > this.model.gameData.win
                        ? this.model.gameData.accumulatedRoundWin
                        : this.model.gameData.win;
            }

            totalWinHandler({
                winLevels: winLevels,
                timeline: this.flowPresentationTimeline,
                winScreen: this.winView.totalWinScreen,
                flowStateController: this.controller,
                winIsBelowStake: isAccumulatedWinBelowStake,
                reels: this.DDDReels,
                keypadPlugin: this.keypad,
            });

            this.xNudgeDeadTease();

            const winType = getWinLevel(
                this.model.gameData.win / this.model.gameData.playedBetValue
            ).winType;

            if (winType === WinType.BIG || winType === WinType.MAX_CAP) {
                this.keypad.setWin(-1, false, false, true);
            } else {
                if (isAccumulatedWinBelowStake) {
                    this.keypad.setWin(winValue, false, true, false);
                }
            }

            this.flowPresentationTimeline.call(
                () => {
                    this.keypad.setWin(
                        this.model.gameData.accumulatedRoundWin,
                        !isAccumulatedWinBelowStake,
                        isAccumulatedWinBelowStake,
                        isAccumulatedWinBelowStake
                    );
                },
                [],
                TimelineLabels.TEXT_COUNT_COMPLETE
            );

            if (this.model.gameData.brokeTheBank) {
                this.flowPresentationTimeline.call(
                    () => {
                        this.keypad.setWin(-1);
                        this.keypad.setZeroBetSpinCounter(-1);
                        this.hideFastSpinButton();
                    },
                    undefined,
                    `${LevelName.MAX_CAP}`
                );
            }

            this.flowPresentationTimeline.resume();
        }
    }

    protected override addSpecialAnimations(isAccumulatedWinBelow: boolean = false) {
        let outroTimeline: gsap.core.Timeline = gsap.timeline({});

        const addOutroSkip = () => {
            const skipToNextLevel = () => {
                const nextLabel: string = outroTimeline.nextLabel();

                outroTimeline.seek(outroTimeline.labels[nextLabel], false);
            };

            this.controller?.setSkipCallback(skipToNextLevel);
        };

        switch (this.model.gameData.nextMode) {
            case AbstractGameMode.NORMAL: {
                this.keypad.setZeroBetSpinCounter(-1);

                let winValue = this.model.gameData.win;
                if (this.model.gameData.brokeTheBank) {
                    winValue =
                        this.model.gameData.accumulatedRoundWin > this.model.gameData.win
                            ? this.model.gameData.accumulatedRoundWin
                            : this.model.gameData.win;
                }
                let winMultiplier: number = winValue / this.model.gameData.playedBetValue;

                const winLevel: IWinLevel = winLevels.filter(
                    (level: IWinLevel) => level.min <= winMultiplier && level.max > winMultiplier
                )[0];

                if (winLevel.name !== LevelName.MAX_CAP) {
                    addOutroSkip();

                    outroTimeline = this.winView.showFreespinOutroScreen(
                        this.model.gameData.accumulatedRoundWin,
                        this.onOutroScreenComplete.bind(this, isAccumulatedWinBelow),
                        this.model.data.mode === AbstractGameMode.SUPER_FREESPIN,
                        !isAccumulatedWinBelow,
                        this.controller
                    );
                } else {
                    this.onOutroScreenComplete(isAccumulatedWinBelow);
                }

                break;
            }
            default: {
                this.finishPresentation();

                break;
            }
        }
    }

    protected onOutroScreenComplete(isWinBelowStake: boolean) {
        this.keypad.setWin(this.model.gameData.accumulatedRoundWin, true, isWinBelowStake);
        this.controller?.setSkipCallback(undefined);
        this.showFastSpinButton();
        this.finishPresentation();
    }

    protected handleEvents(): void {
        super.handleEvents();

        EventsManager.on(ReelsEvents.GET_REELS_VIEW, (reelsView: DDDReelsView) => {
            this.reelsView = reelsView;
        });
    }
}
